import { EXTERNAL_CONNECTION_CALENDARS_DOC } from '@griply/services';
import { ExternalConnectionCalendarsCache } from '@griply/types';
import {
  QueryClient,
  QueryFunction,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { createSubscriptionDoc } from 'shared/lib/@tanstack-query';
import {
  getExternalConnectionCalendars,
  getExternalConnectionCalendarsDoc,
} from 'shared/services/backend-api';
import { getErrorReporter } from 'shared/services/error-tracking';
import { ID } from 'shared/types/id';

export const queryKey = 'external-connection-calendars';

// Snapshot first; the callable fallback write-throughs the cache backend-side, so the snapshot takes over by itself
export const createExternalConnectionCalendarsQueryFn =
  (
    queryClient: QueryClient,
    externalConnectionId: ID,
  ): QueryFunction<ExternalConnectionCalendarsCache | null, string[]> =>
  async (context) => {
    try {
      const cached =
        await createSubscriptionDoc<ExternalConnectionCalendarsCache>(() =>
          getExternalConnectionCalendarsDoc(externalConnectionId),
        )(context);
      if (cached) {
        return cached;
      }
    } catch {
      // cache doc unreadable (backend/rules not rolled out yet) — the callable still answers
    }

    try {
      const calendars =
        await getExternalConnectionCalendars(externalConnectionId);
      return {
        id: EXTERNAL_CONNECTION_CALENDARS_DOC,
        calendars,
        updatedAt: null,
      };
    } catch (error) {
      getErrorReporter().captureException(error, {
        tags: { source: 'external-connections-get-calendars' },
      });
      return (
        queryClient.getQueryData<ExternalConnectionCalendarsCache | null>([
          queryKey,
          externalConnectionId,
        ]) ?? null
      );
    }
  };

export const useExternalConnectionCalendarsQuery = (
  externalConnectionId: ID,
) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [queryKey, externalConnectionId],
    queryFn: createExternalConnectionCalendarsQueryFn(
      queryClient,
      externalConnectionId,
    ),
  });
};
